import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import type {
  ClickUIElementInputProperties,
  IEngagementTeaser,
  IEngagementUIElement,
  IEngagementWidget,
  IImpressionTeaser,
  IImpressionUIElement,
  IImpressionWidget,
  IViewArticle,
  IViewError,
  IViewFrontpage,
  IViewListing,
  IViewLockedArticle,
  IViewPage,
  IViewWeather,
  Page,
  Tracker,
  TrackerEventInput,
  ViewArticleInputProperties,
  ViewListingInputProperties,
  ViewPageInputProperties,
  ViewSalesPosterInputProperties,
} from '@schibsted/pulse-sdk';
import type { PulseExperiment } from '../../../../public-src/core/js/pulse/pulse-util.js';
import type {
  PulseContextInputParams,
  PulseContextProviderConfig,
  PulseContextProviderProps,
  PulseContextValue,
} from './types/index.js';

import { getPulseTracker } from '../../../../public-src/core/js/pulse/pulse-tracker.server.js';
import {
  createPulseObject,
  createPulseTarget,
  getPulseMeta,
} from '../../../../public-src/core/js/pulse/pulse-util.js';
import { getTeaserMetrics } from './utils/metrics.js';
import { buildEngagementClickEvent } from './event/event-builders/build-engagement-click-event.js';
import { buildImpressionEvent } from './event/event-builders/build-impression-event.js';
import { buildProviderInput } from './event/event-builders/build-provider-input.js';
import { buildViewEvent } from './event/event-builders/build-view-event.js';
import { provideFakePulseAutotracker } from './pulse-autotracker-fake.server.js';

const withLegacyPage = <T extends { page?: Page }>(
  object: T,
  page: Page | null,
): T => (page ? { ...object, page } : object);

const PulseContext = createContext<PulseContextValue | null>(null);

const PulseContextProvider: React.FC<PulseContextProviderProps> = ({
  config,
  children,
  experiments = [],
  isTpaasEnabled,
}) => {
  const [pulseTracker, setPulseTracker] = useState<Tracker | null>(null);

  // TPaaS view events overwrite the tracker's current page, which legacy
  // UI element events read. Keep the page from the legacy view page event so
  // those keep their original page id.
  const legacyPageRef = useRef<Page | null>(null);

  const { providerId, productTag, targetNewsroom } = config;

  const providerInput = buildProviderInput(config);

  useEffect(() => {
    if (!pulseTracker) {
      getPulseTracker(providerId, productTag).then((tracker) => {
        setPulseTracker(tracker);

        provideFakePulseAutotracker(tracker);

        const baseExperiments = window.PULSE_EXPERIMENTS || [];

        const isFrontpage = () => window.location.pathname === '/';
        if (isFrontpage()) {
          const { experiment } = window.CURATE_CONFIG || {};
          if (experiment) {
            baseExperiments.push(experiment);
          }
        }
        // Add experiments right away since we can't know if view frontpage
        // fires before the other useEffect for updating experiments
        tracker.update({
          experiments: baseExperiments.concat(experiments),
        });
      });
    }
  }, [pulseTracker, providerId, productTag, experiments]);

  const registerPageLeaveTracking = useCallback(
    ({
      objectElement,
      pageElement = document.querySelector('body') ?? undefined,
    }: {
      objectElement: Element;
      pageElement?: Element;
    }) => {
      if (pulseTracker && pageElement) {
        pulseTracker.addPageLeaveTracking(objectElement, pageElement);
      }
    },
    [pulseTracker],
  );

  const getEnvironmentId = useCallback(() => {
    if (pulseTracker) {
      return pulseTracker.getEnvironmentId();
    }

    return Promise.resolve(null);
  }, [pulseTracker]);

  const registerViewElementTracking = useCallback(() => {
    if (!pulseTracker) {
      return;
    }

    pulseTracker.addElementVisibilityObserver(
      '[data-pulse-entity-id]',
      (element) => {
        const pulseMeta = getPulseMeta(element);
        const target = createPulseTarget(pulseMeta, providerId, targetNewsroom);
        const object = createPulseObject(pulseMeta);

        const { variant } = window.CURATE_CONFIG || {};

        const metrics = getTeaserMetrics(variant, pulseMeta);
        const teaserExperiments = [...(pulseMeta.experiments || [])];

        pulseTracker.trackLegacyViewUIElement({
          object: legacyPageRef.current
            ? { ...object, page: legacyPageRef.current }
            : object,
          target,
          metrics,
          experiments: teaserExperiments,
        });

        const objectType = element.dataset.trackObjectType;
        const tpaas = element.dataset.trackEvent;

        if (!isTpaasEnabled || !tpaas) {
          return;
        }

        if (objectType === 'Teaser') {
          pulseTracker.trackImpressionTeaser(
            buildImpressionEvent<IImpressionTeaser>({
              elementType: 'Teaser',
              event: tpaas,
              providerId,
              providerInput,
            }),
          );
        } else if (objectType === 'Widget') {
          pulseTracker.trackImpressionWidget(
            buildImpressionEvent<IImpressionWidget>({
              elementType: 'Widget',
              event: tpaas,
              providerId,
              providerInput,
            }),
          );
        } else if (objectType === 'UIElement') {
          pulseTracker.trackImpressionUIElement(
            buildImpressionEvent<IImpressionUIElement>({
              elementType: 'UIElement',
              event: tpaas,
              providerId,
              providerInput,
            }),
          );
        }
      },
    );
  }, [pulseTracker, providerId, targetNewsroom, providerInput, isTpaasEnabled]);

  const trackClickTeaser = useCallback(
    (
      legacy: ClickUIElementInputProperties,
      tpaas: PulseContextInputParams<IEngagementTeaser>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyClickUIElement(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackEngagementTeaser(
        buildEngagementClickEvent<IEngagementTeaser>({
          elementType: 'Teaser',
          event: tpaas,
          providerId,
          providerInput,
        }),
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackClickUIElement = useCallback(
    (
      legacy: ClickUIElementInputProperties,
      tpaas: PulseContextInputParams<IEngagementUIElement>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyClickUIElement({
        ...legacy,
        object: withLegacyPage(legacy.object, legacyPageRef.current),
      });

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackEngagementUIElement(
        buildEngagementClickEvent<IEngagementUIElement>({
          elementType: 'UIElement',
          event: tpaas,
          providerId,
          providerInput,
        }),
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackClickWidget = useCallback(
    (
      legacy: ClickUIElementInputProperties,
      tpaas: PulseContextInputParams<IEngagementWidget>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyClickUIElement(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackEngagementWidget(
        buildEngagementClickEvent<IEngagementWidget>({
          elementType: 'Widget',
          event: tpaas,
          providerId,
          providerInput,
        }),
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewArticle = useCallback(
    (
      legacy: ViewArticleInputProperties,
      tpaas: PulseContextInputParams<IViewArticle>,
      objectElement: Element,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewArticle(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewArticle(
        buildViewEvent<IViewArticle>({
          pageType: 'Article',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: { objectElement } },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  // Note: We fire Listing for legacy, but Frontpage for TPaaS
  const trackViewFrontpage = useCallback(
    (
      legacy: ViewListingInputProperties,
      tpaas: PulseContextInputParams<IViewFrontpage>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewListing(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewFrontpage(
        buildViewEvent<IViewFrontpage>({
          pageType: 'Frontpage',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: false },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewListing = useCallback(
    (
      legacy: ViewListingInputProperties,
      tpaas: PulseContextInputParams<IViewListing>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewListing(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewListing(
        buildViewEvent<IViewListing>({
          pageType: 'Listing',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: false },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewPage = useCallback(
    (
      legacy: ViewPageInputProperties,
      tpaas: PulseContextInputParams<IViewPage>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewPage(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewPage(
        buildViewEvent<IViewPage>({
          pageType: 'Page',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: false },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewError = useCallback(
    (
      legacy: ViewPageInputProperties,
      tpaas: PulseContextInputParams<IViewError>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewPage(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewError(
        buildViewEvent<IViewError>({
          pageType: 'Error',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: false },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewLockedArticle = useCallback(
    (
      legacy: ViewSalesPosterInputProperties,
      tpaas: PulseContextInputParams<IViewLockedArticle>,
      objectElement: Element,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.trackLegacyViewSalesPoster(legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewLockedArticle(
        buildViewEvent<IViewLockedArticle>({
          pageType: 'LockedArticle',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: { objectElement } },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackViewWeather = useCallback(
    (
      legacy: Partial<TrackerEventInput>,
      tpaas: PulseContextInputParams<IViewWeather>,
    ): void => {
      if (!pulseTracker) {
        return;
      }

      pulseTracker.track('trackerEvent', legacy);

      if (!isTpaasEnabled) {
        return;
      }

      pulseTracker.trackViewWeather(
        buildViewEvent<IViewWeather>({
          pageType: 'Weather',
          event: tpaas,
          providerId,
          providerInput,
        }),
        { leaveTracking: false },
      );
    },
    [pulseTracker, providerInput, providerId, isTpaasEnabled],
  );

  const trackLegacyViewPage = useCallback(
    (legacy: ViewPageInputProperties): void => {
      if (!pulseTracker) {
        return;
      }

      Promise.resolve(legacy.object).then(({ pageId }) => {
        legacyPageRef.current = {
          id: `sdrn:${providerId}:page:${pageId}`,
          type: 'Page',
          url: window.location.href,
        };
      });

      pulseTracker.trackLegacyViewPage(legacy);
    },
    [pulseTracker, providerId],
  );

  return (
    <PulseContext.Provider
      value={{
        registerViewElementTracking,
        registerPageLeaveTracking,
        getEnvironmentId,
        trackClickTeaser,
        trackClickUIElement,
        trackClickWidget,
        trackViewArticle,
        trackViewFrontpage,
        trackViewListing,
        trackViewPage,
        trackViewError,
        trackViewLockedArticle,
        trackViewWeather,
        trackLegacyViewPage,
      }}
    >
      {children}
    </PulseContext.Provider>
  );
};

const usePulseContext = (): PulseContextValue => {
  const context = useContext(PulseContext);

  if (!context) {
    throw new Error('No Pulse context provided');
  }

  return context;
};

type WithPulseProps = {
  experiments?: PulseExperiment[];
  isTpaasEnabled?: boolean;
};

const withPulse = <P extends Record<string, unknown>>(
  Component: React.FC<P>,
  pulseConfig: PulseContextProviderConfig,
): React.FC<Omit<P, keyof WithPulseProps> & WithPulseProps> => {
  const Hoc = (props: Omit<P, keyof WithPulseProps> & WithPulseProps) => {
    const { experiments, isTpaasEnabled = false, ...rest } = props;

    return (
      <PulseContextProvider
        config={pulseConfig}
        experiments={experiments}
        isTpaasEnabled={isTpaasEnabled}
      >
        <Component {...(rest as P)} />
      </PulseContextProvider>
    );
  };

  Hoc.displayName = `withPulse<${Component.displayName}>`;

  return Hoc;
};

export { usePulseContext, withPulse };
